import type {
  Contact,
  ContactImportJob,
  ContactImportReport,
  ContactList,
  ContactMergeResult,
  ContactMergeSuggestion,
  Segment,
  SegmentPreview,
  ContactDetail,
  CrmPage,
  Note,
  ContactFact,
  Task,
  TimelineEvent,
  ContactsPage,
  ContactsQuery,
  ContactsSummary,
  CustomField,
  Label,
  SimilarLabelPair,
  ContactLabelsResponse,
  WhatsAppCloudBlockedContact,
} from "../types";
import type {
  MigrationJob,
  MigrationSource,
  MigrationChecklistStep,
  MigrationTemplate,
} from "../migration-types";
import type { ApiClient } from "../api-client";
import type { ContactsExportJob } from "../methods/integrations";
import type { HttpTransport } from "../http/transport";
import type { LinkPreviewData, LabelSuggestionData, GlobalSearchResults } from "../client-types";

export type ContactsApiMethods = Pick<
  ApiClient,
  | "listContacts"
  | "getContactsSummary"
  | "createContact"
  | "getLinkPreview"
  | "globalSearch"
  | "listLabelSuggestions"
  | "resolveLabelSuggestion"
  | "getContact"
  | "updateContact"
  | "listNotes"
  | "createNote"
  | "listContactFacts"
  | "createContactFact"
  | "dismissContactFact"
  | "listTasks"
  | "createTask"
  | "updateTask"
  | "getContactTimeline"
  | "getContactLabels"
  | "addContactLabel"
  | "removeContactLabel"
  | "bulkContactLabels"
  | "listSegments"
  | "createSegment"
  | "updateSegment"
  | "deleteSegment"
  | "previewSegment"
  | "mergeContact"
  | "listMergeSuggestions"
  | "listSuggestedContactIds"
  | "mergeSuggestion"
  | "dismissMergeSuggestion"
  | "undoContactMerge"
  | "listContactLists"
  | "addContactToList"
  | "createContactList"
  | "deleteContactList"
  | "exportContacts"
  | "startContactsExport"
  | "getContactsExportJob"
  | "downloadContactsExport"
  | "exportSegment"
  | "exportContactList"
  | "bulkContactAction"
  | "bulkDeleteContacts"
  | "migration"
  | "dryRunContactImport"
  | "runContactImport"
  | "listLabels"
  | "createLabel"
  | "updateLabel"
  | "deleteLabel"
  | "listSimilarLabels"
  | "mergeLabels"
  | "listCustomFields"
  | "createCustomField"
  | "updateCustomField"
  | "deleteCustomField"
  | "blockWhatsAppContact"
  | "unblockWhatsAppContact"
>;

function contactsQueryParams(
  query: ContactsQuery | undefined,
): Record<string, string | number | undefined> {
  const { labelIds, excludeLabelIds, channels, ...rest } = query ?? {};
  return {
    ...(rest as Record<string, string | number | undefined>),
    ...(labelIds?.length ? { labelIds: labelIds.join(",") } : {}),
    ...(excludeLabelIds?.length ? { excludeLabelIds: excludeLabelIds.join(",") } : {}),
    ...(channels?.length ? { channels: channels.join(",") } : {}),
  };
}

export function createContactsApi({
  request,
  requestCsv,
  ws,
  queryString,
}: HttpTransport): ContactsApiMethods {
  return {
    listContacts: (id, query) =>
      request<ContactsPage>("GET", `${ws(id)}/contacts${queryString(contactsQueryParams(query))}`),
    getContactsSummary: (id, query) =>
      request<ContactsSummary>(
        "GET",
        `${ws(id)}/contacts/summary${queryString(contactsQueryParams(query))}`,
      ),
    createContact: (id, input) => request<Contact>("POST", `${ws(id)}/contacts`, input),
    globalSearch: (id, q, limit) =>
      request<GlobalSearchResults>(
        "GET",
        `${ws(id)}/search${queryString({ q, ...(limit ? { limit } : {}) })}`,
      ),
    getLinkPreview: (id, url) =>
      request<{ preview: LinkPreviewData | null }>(
        "GET",
        `${ws(id)}/conversations/link-preview?url=${encodeURIComponent(url)}`,
      ),
    listLabelSuggestions: (id, conversationId) =>
      request<{ items: readonly LabelSuggestionData[] }>(
        "GET",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/label-suggestions`,
      ),
    resolveLabelSuggestion: async (id, conversationId, suggestionId, action) => {
      await request<unknown>(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/label-suggestions/${encodeURIComponent(suggestionId)}/${action}`,
        {},
      );
    },
    getContact: (id, contactId) =>
      request<ContactDetail>("GET", `${ws(id)}/contacts/${encodeURIComponent(contactId)}`),
    updateContact: (id, contactId, input) =>
      request<Contact>("PATCH", `${ws(id)}/contacts/${encodeURIComponent(contactId)}`, input),
    listNotes: (id, subjectType, subjectId, cursor) =>
      request<CrmPage<Note>>(
        "GET",
        `${ws(id)}/notes${queryString({ subjectType, subjectId, cursor })}`,
      ),
    createNote: (id, input) => request<Note>("POST", `${ws(id)}/notes`, input),
    listContactFacts: (id, contactId) =>
      request<{ items: readonly ContactFact[] }>(
        "GET",
        `${ws(id)}/contacts/${encodeURIComponent(contactId)}/facts`,
      ),
    createContactFact: (id, contactId, input) =>
      request<ContactFact>(
        "POST",
        `${ws(id)}/contacts/${encodeURIComponent(contactId)}/facts`,
        input,
      ),
    dismissContactFact: async (id, contactId, factId) => {
      await request<unknown>(
        "POST",
        `${ws(id)}/contacts/${encodeURIComponent(contactId)}/facts/${encodeURIComponent(factId)}/dismiss`,
        {},
      );
    },
    listTasks: (id, query) =>
      request<CrmPage<Task>>(
        "GET",
        `${ws(id)}/tasks${queryString(query as Record<string, string | undefined>)}`,
      ),
    createTask: (id, input) => request<Task>("POST", `${ws(id)}/tasks`, input),
    updateTask: (id, taskId, input) =>
      request<Task>("PATCH", `${ws(id)}/tasks/${encodeURIComponent(taskId)}`, input),
    getContactTimeline: (id, contactId, query) =>
      request<CrmPage<TimelineEvent>>(
        "GET",
        `${ws(id)}/contacts/${encodeURIComponent(contactId)}/timeline${queryString({
          cursor: query.cursor,
          types: query.types?.length ? query.types.join(",") : undefined,
        })}`,
      ),
    getContactLabels: (id, contactId) =>
      request<ContactLabelsResponse>(
        "GET",
        `${ws(id)}/contacts/${encodeURIComponent(contactId)}/labels`,
      ),
    async addContactLabel(id, contactId, labelId) {
      await request(
        "POST",
        `${ws(id)}/contacts/${encodeURIComponent(contactId)}/labels/${encodeURIComponent(labelId)}`,
      );
    },
    async removeContactLabel(id, contactId, labelId) {
      await request(
        "DELETE",
        `${ws(id)}/contacts/${encodeURIComponent(contactId)}/labels/${encodeURIComponent(labelId)}`,
      );
    },
    bulkContactLabels: (id, input) =>
      request<{ requested: number; changed: number }>(
        "POST",
        `${ws(id)}/contacts/bulk/labels`,
        input,
      ),
    listSegments: async (id) =>
      (await request<{ items: Segment[] }>("GET", `${ws(id)}/segments`)).items,
    createSegment: (id, input) => request<Segment>("POST", `${ws(id)}/segments`, input),
    updateSegment: (id, segmentId, input) =>
      request<Segment>("PATCH", `${ws(id)}/segments/${encodeURIComponent(segmentId)}`, input),
    async deleteSegment(id, segmentId) {
      await request("DELETE", `${ws(id)}/segments/${encodeURIComponent(segmentId)}`);
    },
    previewSegment: (id, filterDefinition) =>
      request<SegmentPreview>("POST", `${ws(id)}/segments/preview`, { filterDefinition }),
    mergeContact: (id, contactId, input) =>
      request<ContactMergeResult>(
        "POST",
        `${ws(id)}/contacts/${encodeURIComponent(contactId)}/merge`,
        input,
      ),
    listMergeSuggestions: async (id, contactId) =>
      (
        await request<{ items: ContactMergeSuggestion[] }>(
          "GET",
          `${ws(id)}/contacts/${encodeURIComponent(contactId)}/merge-suggestions`,
        )
      ).items,
    listSuggestedContactIds: async (id) =>
      (
        await request<{ contactIds: string[] }>(
          "GET",
          `${ws(id)}/contacts/merge-suggestions/contact-ids`,
        )
      ).contactIds,
    mergeSuggestion: (id, suggestionId) =>
      request<ContactMergeResult>(
        "POST",
        `${ws(id)}/contacts/merge-suggestions/${encodeURIComponent(suggestionId)}/merge`,
      ),
    async dismissMergeSuggestion(id, suggestionId) {
      await request(
        "POST",
        `${ws(id)}/contacts/merge-suggestions/${encodeURIComponent(suggestionId)}/dismiss`,
      );
    },
    async undoContactMerge(id, undoId) {
      await request("POST", `${ws(id)}/contacts/merges/${encodeURIComponent(undoId)}/undo`);
    },
    listContactLists: async (id) =>
      (await request<{ items: ContactList[] }>("GET", `${ws(id)}/lists`)).items,
    async addContactToList(id, listId, contactId) {
      await request("POST", `${ws(id)}/lists/${encodeURIComponent(listId)}/members`, {
        contactIds: [contactId],
      });
    },
    createContactList: (id, name) => request<ContactList>("POST", `${ws(id)}/lists`, { name }),
    async deleteContactList(id, listId) {
      await request("DELETE", `${ws(id)}/lists/${encodeURIComponent(listId)}`);
    },
    exportContacts: (id, selection) =>
      requestCsv("POST", `${ws(id)}/contacts/export`, { selection }),
    startContactsExport: (id, query) =>
      request<ContactsExportJob>("POST", `${ws(id)}/contacts/export-jobs`, {
        filters: contactsQueryParams(query),
      }),
    getContactsExportJob: (id, jobId) =>
      request<ContactsExportJob>(
        "GET",
        `${ws(id)}/contacts/export-jobs/${encodeURIComponent(jobId)}`,
      ),
    downloadContactsExport: (_id, downloadUrl) => requestCsv("GET", downloadUrl),
    exportSegment: (id, segmentId) =>
      requestCsv("GET", `${ws(id)}/segments/${encodeURIComponent(segmentId)}/export`),
    exportContactList: (id, listId) =>
      requestCsv("GET", `${ws(id)}/lists/${encodeURIComponent(listId)}/export`),
    bulkContactAction: (id, selection, action) =>
      request<{ matched: number; changed: number }>("POST", `${ws(id)}/contacts/bulk/actions`, {
        selection,
        action,
      }),
    bulkDeleteContacts: (id, selection) =>
      request<{ matched: number; changed: number }>("POST", `${ws(id)}/contacts/bulk/delete`, {
        selection,
      }),
    migration: {
      autoReplyPreview: (id, json) =>
        request("POST", `${ws(id)}/migrations/flows/preview`, { json }),
      xitfbPreview: (id, files) => request("POST", `${ws(id)}/migration/xitfb/preview`, { files }),
      xitfbImport: (id, files) => request("POST", `${ws(id)}/migration/xitfb/import`, { files }),
      sources: async (id) =>
        (await request<{ items: MigrationSource[] }>("GET", `${ws(id)}/migrations/sources`)).items,
      start: (id, input) => request<MigrationJob>("POST", `${ws(id)}/migrations`, input),
      update: (id, jobId, patch) =>
        request<MigrationJob>("PATCH", `${ws(id)}/migrations/${encodeURIComponent(jobId)}`, patch),
      dryRun: (id, jobId) =>
        request<MigrationJob>("POST", `${ws(id)}/migrations/${encodeURIComponent(jobId)}/dry-run`),
      revalidate: (id, jobId) =>
        request<{ job: MigrationJob; changed: boolean }>(
          "POST",
          `${ws(id)}/migrations/${encodeURIComponent(jobId)}/revalidate`,
        ),
      commit: (id, jobId) =>
        request<MigrationJob>("POST", `${ws(id)}/migrations/${encodeURIComponent(jobId)}/commit`),
      rollback: (id, jobId) =>
        request<unknown>("POST", `${ws(id)}/migrations/${encodeURIComponent(jobId)}/rollback`),
      reimport: (id, jobId, csv) =>
        request<MigrationJob>(
          "POST",
          `${ws(id)}/migrations/${encodeURIComponent(jobId)}/reimport`,
          { confirm: true, csv },
        ),
      reconnectChecklist: async (id, source) =>
        (
          await request<{ items: MigrationChecklistStep[] }>(
            "GET",
            `${ws(id)}/migrations/reconnect-checklist?source=${encodeURIComponent(source)}`,
          )
        ).items,
      reportUrl: (id, jobId, format) =>
        `${ws(id)}/migrations/${encodeURIComponent(jobId)}/report?format=${format}`,
      templates: async (id, source) =>
        (
          await request<{ items: MigrationTemplate[] }>(
            "GET",
            `${ws(id)}/migration-templates${source ? `?source=${encodeURIComponent(source)}` : ""}`,
          )
        ).items,
      saveTemplate: (id, input) =>
        request<MigrationTemplate>("POST", `${ws(id)}/migration-templates`, input),
    },
    async dryRunContactImport(id, csv, options) {
      return (
        await request<{ report: ContactImportReport }>("POST", `${ws(id)}/contacts/import`, {
          csv,
          options,
          dryRun: true,
        })
      ).report;
    },
    async runContactImport(id, csv, options) {
      return (
        await request<{ job: ContactImportJob }>("POST", `${ws(id)}/contacts/import`, {
          csv,
          options,
        })
      ).job;
    },
    async listLabels(id) {
      return (await request<{ items: Label[] }>("GET", `${ws(id)}/labels`)).items;
    },
    createLabel: (id, input) => request<Label>("POST", `${ws(id)}/labels`, input),
    updateLabel: (id, labelId, input) =>
      request<Label>("PATCH", `${ws(id)}/labels/${encodeURIComponent(labelId)}`, input),
    async deleteLabel(id, labelId) {
      await request("DELETE", `${ws(id)}/labels/${encodeURIComponent(labelId)}`);
    },
    async listSimilarLabels(id) {
      return (await request<{ items: SimilarLabelPair[] }>("GET", `${ws(id)}/labels/similar`))
        .items;
    },
    mergeLabels: (id, input) =>
      request<{ retagged: number }>("POST", `${ws(id)}/labels/merge`, input),
    async listCustomFields(id) {
      return (await request<{ items: CustomField[] }>("GET", `${ws(id)}/custom-fields`)).items;
    },
    createCustomField: (id, input) =>
      request<CustomField>("POST", `${ws(id)}/custom-fields`, input),
    updateCustomField: (id, fieldId, input) =>
      request<CustomField>(
        "PATCH",
        `${ws(id)}/custom-fields/${encodeURIComponent(fieldId)}`,
        input,
      ),
    async deleteCustomField(id, fieldId) {
      await request("DELETE", `${ws(id)}/custom-fields/${encodeURIComponent(fieldId)}`);
    },
    async blockWhatsAppContact(id, channelId, phone) {
      return (
        await request<{ block: WhatsAppCloudBlockedContact }>(
          "POST",
          `${ws(id)}/channels/whatsapp-cloud/${encodeURIComponent(channelId)}/block`,
          { phone },
        )
      ).block;
    },
    async unblockWhatsAppContact(id, channelId, phone) {
      await request(
        "POST",
        `${ws(id)}/channels/whatsapp-cloud/${encodeURIComponent(channelId)}/unblock`,
        { phone },
      );
    },
  };
}
